<template lang="pug">
div.px-8
  v-row(no-gutters)
    v-col.pr-8.shrink
      div.sticky.white(style="width: 230px; top: 106px;")
        .text-md.font-semibold.neutral500--text.px-6.py-2.round-md.mb-2.clickable(@click="$router.push('/admin/clinic/settings/price-tier-settings')") Price tier
        .text-md.font-semibold.neutral200.px-6.py-2.round-md.mb-2 Medicine details
        .text-md.font-semibold.neutral500--text.px-6.py-2.round-md.mb-2.clickable(@click="$router.push('/admin/clinic/settings/supplier-settings')") Suppliers

    v-col.grow
      v-container.pt-10
        v-breadcrumbs.pt-0.px-0.pb-3(:items="breadcrumbItems")
          template(v-slot:item="{ item }")
            a.text-sm.font-medium.no-underline.neutral700--text(v-if="!item.disabled" @click="$router.push(item.href)") {{ item.text }}
            .text-sm.neutral700--text(v-else) {{ item.text }}

        .text-2xl.font-semibold.neutral700--text Inventory and services settings
        v-divider.mt-2
        .text-lg.font-medium.neutral700--text.mt-4 Medicine details
        .text-sm.neutral700--text Setup your drug group, medicine dosage and instructions.

        v-row.mt-6.pb-2.pb-md-0(no-gutters)
          v-col(cols="12" md="8")
            v-tabs(v-model="tabs" color="basic700" :height="44" :grow="isMobileScreen" grow)
              v-tab.text-sm.font-semibold.text-none.px-0 Drug group
              v-tab.text-sm.font-semibold.text-none.px-0 Instruction
              v-tab.text-sm.font-semibold.text-none.px-0 Precaution
              v-tab.text-sm.font-semibold.text-none.px-0 Frequency
              v-tab.text-sm.font-semibold.text-none.px-0 Duration
              v-tab.text-sm.font-semibold.text-none.px-0 Dosage unit
              v-tab.text-sm.font-semibold.text-none.px-0 Indication
          v-col(cols="12" md="4" align="end")
            .d-flex.align-center
              v-spacer
              v-btn(@click="clickSearch" icon small color="neutral500")
                v-icon(size="20") $yzi-search-sm
              v-text-field(v-if="showSearchField" v-model="searchText" @blur="blurSearch" @input="queryChanged" solo dense flat placeholder="Type to search..." hide-details clearable autofocus)
              v-btn.button-sm.ml-4(color="neutral700" dark depressed @click="addNewMedications") Add new
        v-divider

        .data-table
          v-data-table.mt-sm-4.pb-6(:headers="headers" :items="medications" item-key="id" :items-per-page="pageSize" hide-default-footer :loading="isFetching" :disable-sort="isMobileScreen" :header-props="{ sortIcon: '$yzi-triangle' }")
            //- name
            template(v-slot:item.name="{ item }")
              .text-sm.neutral700--text {{ item.value }}
            //- status
            template(v-slot:item.status="{ item }")
              div
                y-switch.mb-n3.ml-n3(v-model="item.enabled" @change="updateStatus(item, item.enabled)")
            //- actions
            template(v-slot:item.actions="{ item, index }")
              div.d-flex
                v-spacer
                v-menu(offset-y :top="menuPositionTop(index)" left attach rounded="lg")
                  template(#activator="{ on }")
                    v-btn.mr-3(icon small v-on="on")
                      v-icon mdi-dots-horizontal
                  v-list.py-0(width="100")
                    v-list-item(@click="editItem(item)")
                      v-list-item-title.text-sm.neutral700--text Edit
                    v-divider
                    v-list-item(@click="deleteItem(item)")
                      v-list-item-title.text-sm.danger500--text Delete

        div.d-flex.align-center(v-if="totalCount > pageSize")
          v-spacer
          yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" attach menu-top @input="pageChanged")

  .dialogs
    add-group-dialog(v-if="openAddGroupsDialog" :open.sync="openAddGroupsDialog" :parentGroup="selectedMedication" @closed="closed" @reset="reset")
    add-instruction-dialog(v-if="openAddInstructionsDialog" :open.sync="openAddInstructionsDialog" :parentInstruction="selectedMedication" @closed="closed" @reset="reset")
    add-precaution-dialog(v-if="openAddPrecautionsDialog" :open.sync="openAddPrecautionsDialog" :parentPrecaution="selectedMedication" @closed="closed" @reset="reset")
    add-frequency-dialog(v-if="openAddFrequenciesDialog" :open.sync="openAddFrequenciesDialog" :parentFrequencies="selectedMedication" @closed="closed" @reset="reset")
    add-duration-dialog(v-if="openAddDurationsDialog" :open.sync="openAddDurationsDialog" :parentDuration="selectedMedication" @closed="closed" @reset="reset")
    add-dosage-dialog(v-if="openAddDosagesDialog" :open.sync="openAddDosagesDialog" :parentDosage="selectedMedication" @closed="closed" @reset="reset")
    add-indication-dialog(v-if="openAddIndicationDialog" :open.sync="openAddIndicationDialog" :parentIndication="selectedMedication" @closed="closed" @reset="reset")
    delete-confirmation-dialog(v-if="openDeleteDialog" :open.sync="openDeleteDialog" :loading="isLoading" @cancel="closed" @delete="confirmDelete")
      .text-lg.font-medium Are you sure you want to delete this item?
      .text-md.mt-4 This action will be done immediately and can’t be undone. Click “Confirm” to proceed

</template>

<script>
import { debounce } from '@/utils'
import { mapGetters, mapActions } from 'vuex'
import AddGroupDialog from '@/components/clinic/settings/AddGroupDialog.vue'
import AddInstructionDialog from '@/components/clinic/settings/AddInstructionDialog.vue'
import AddPrecautionDialog from '@/components/clinic/settings/AddPrecautionDialog.vue'
import AddFrequencyDialog from '@/components/clinic/settings/AddFrequencyDialog.vue'
import AddDurationDialog from '@/components/clinic/settings/AddDurationDialog.vue'
import AddDosageDialog from '@/components/clinic/settings/AddDosageDialog.vue'
import AddIndicationDialog from '@/components/clinic/settings/AddIndicationDialog.vue'
import DeleteConfirmationDialog from '@/components/clinic/dialogs/DeleteConfirmationDialog.vue'

export default {
  components: { AddGroupDialog, AddInstructionDialog, AddPrecautionDialog, AddFrequencyDialog, AddDurationDialog, AddDosageDialog, AddIndicationDialog, DeleteConfirmationDialog },
  data: () => ({
    showSearchField: false,
    isFetching: false,
    isLoading: false,
    openAddGroupsDialog: false,
    openAddInstructionsDialog: false,
    openAddPrecautionsDialog: false,
    openAddFrequenciesDialog: false,
    openAddDurationsDialog: false,
    openAddDosagesDialog: false,
    openAddIndicationDialog: false,
    openDeleteDialog: false,
    selectedMedication: null,
    tabs: 0,
    pageSize: 20,
    searchText: '',
    totalCount: 0,
    currentPage: 1,
    medications: [],
    headers: [
      { text: 'Name', value: 'name', enable: true },
      { text: 'Status', value: 'status', enable: true, width: '100' },
      { text: 'Actions', value: 'actions', enable: true, class: 'text-end', width: '100', sortable: false },
    ],
    breadcrumbItems: [
      {
        text: 'Clinic management',
        disabled: false,
        href: '/admin/clinic/settings'
      },
      {
        text: 'Inventory and services settings',
        disabled: true
      }
    ]
  }),
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    isMobileScreen () {
      return this.$vuetify.breakpoint.xs
    },
  },
  watch: {
    tabs () {
      this.searchText = null
      this.currentPage = 1
      this.fetchMedicationList()
    },
    pageSize () {
      this.currentPage = 1
      this.fetchMedicationList()
    }
  },
  mounted () {
    this.fetchMedicationList()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchMedicationList () {
      this.isFetching = true
      let params = {
        page_size: this.pageSize,
        page: this.currentPage,
        company: this.company.id,
      }
      if (this.tabs === 0) { params.field = 'medication.group' }
      if (this.tabs === 1) { params.field = 'medication.instruction' }
      if (this.tabs === 2) { params.field = 'medication.precaution' }
      if (this.tabs === 3) { params.field = 'medication.frequency' }
      if (this.tabs === 4) { params.field = 'medication.duration' }
      if (this.tabs === 5) { params.field = 'medication.dosage' }
      if (this.tabs === 6) { params.field = 'medication.indication' }
      if (this.searchText) { params.value__contains = this.searchText }
      this.$axios.get(`/options/`, { params })
        .then(resp => {
          this.medications = resp.data.results
          this.totalCount = resp.data.count
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    updateStatus (item, status) {
      this.isFetching = true
      this.$axios.patch(`/options/${item.id}`, { enabled: status })
        .then(() => {
          this.reset()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          this.isFetching = false
        })
    },
    confirmDelete () {
      this.isLoading = true
      this.$axios.delete(`/options/${this.selectedMedication.id}`)
        .then(() => {
          this.reset()
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    queryChanged: debounce(function () {
      this.currentPage = 1
      this.fetchMedicationList()
    }, 750),
    pageChanged () {
      this.fetchMedicationList()
    },
    clickSearch () {
      if (this.showSearchField) {
        if (!this.searchText || this.searchText.length === 0) {
          this.showSearchField = false
        }
      } else {
        this.showSearchField = true 
      }
    },
    blurSearch () {
      if (!this.searchText || this.searchText.length === 0) {
        this.showSearchField = false
      }
    },
    addNewMedications () {
      if (this.tabs === 0) { this.openAddGroupsDialog = true }
      if (this.tabs === 1) { this.openAddInstructionsDialog = true }
      if (this.tabs === 2) { this.openAddPrecautionsDialog = true }
      if (this.tabs === 3) { this.openAddFrequenciesDialog = true }
      if (this.tabs === 4) { this.openAddDurationsDialog = true }
      if (this.tabs === 5) { this.openAddDosagesDialog = true }
      if (this.tabs === 6) { this.openAddIndicationDialog = true }
    },
    editItem (item) {
      this.selectedMedication = item
      if (this.tabs === 0) { this.openAddGroupsDialog = true }
      if (this.tabs === 1) { this.openAddInstructionsDialog = true }
      if (this.tabs === 2) { this.openAddPrecautionsDialog = true }
      if (this.tabs === 3) { this.openAddFrequenciesDialog = true }
      if (this.tabs === 4) { this.openAddDurationsDialog = true }
      if (this.tabs === 5) { this.openAddDosagesDialog = true }
      if (this.tabs === 6) { this.openAddIndicationDialog = true }
    },
    deleteItem (item) {
      this.selectedMedication = item
      this.openDeleteDialog = true
    },
    reset () {
      this.currentPage = 1
      this.fetchMedicationList()
      this.selectedMedication = null
      this.openDeleteDialog = false
      this.openAddGroupsDialog = false
      this.openAddInstructionsDialog = false
      this.openAddPrecautionsDialog = false
      this.openAddFrequenciesDialog = false
      this.openAddDurationsDialog = false
      this.openAddDosagesDialog = false
      this.openAddIndicationDialog = false
    },
    closed () {
      this.selectedMedication = null
      this.openDeleteDialog = false
      this.openAddGroupsDialog = false
      this.openAddInstructionsDialog = false
      this.openAddPrecautionsDialog = false
      this.openAddFrequenciesDialog = false
      this.openAddDurationsDialog = false
      this.openAddDosagesDialog = false
      this.openAddIndicationDialog = false
    },
    menuPositionTop (index) {
      let position = index + 1
      if (position > 6) {
        let start = this.medications.length - 2
        if (position > start) {
          return true
        }
      }
      return false
    },
  }
}
</script>

<style lang="scss" scoped>
</style>